import { LocalizationManager } from "../../localization/localization_manager.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";

export class TournamentTitle
{
  public static readonly TITLE_W = 560;
  public static readonly TITLE_H = 130;
  public static readonly LABEL_W = 600;
  public static readonly LABEL_H = 54;

  private static readonly LAYERS = [
    { outline_width: 42, outline_color: "#6C1F03", outline_offset_y: 2, shadow: false },
    { outline_width: 38, outline_color: "#F4AB26", outline_offset_y: 0, shadow: false },
    { outline_width: 28, outline_color: "#4A0224", outline_offset_y: 2, shadow: false },
    { outline_width: 22, outline_color: "#E95600", outline_offset_y: 0, shadow: false },
    { outline_width: 6,  outline_color: "#AA6600", outline_offset_y: 2, shadow: true },
  ];

  public static shadow( scale = 1 ): { shadow_blur: number; shadow_color: string; shadow_offset_x: number; shadow_offset_y: number }
  {
    return {
      shadow_blur: LayoutScaler.textBorderSizeFromDesign( 35 * scale ),
      shadow_color: "rgba(100, 27, 0, 0.89)",
      shadow_offset_x: 0,
      shadow_offset_y: LayoutScaler.yFromDesign( 5 * scale ),
    };
  }

  /**
   * The layered yellow-on-orange title; other events reuse it with their own key and box.
   * A text too long for the box gets a smaller font up front, with the outlines scaled
   * along: shrinking only the glyphs at render time (scale_to_width) left the five
   * full-size outlines misaligned, and the title read as doubled.
   */
  public static createTitleLayers(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    options: { localization_key?: string; w?: number; h?: number; font_size?: number; wrap_to_two_lines?: boolean } = {}
  ): UITextElement[]
  {
    const localization_key = options.localization_key ?? "TOURNAMENT.TITLE";
    const box_w = options.w ?? TournamentTitle.TITLE_W;
    const box_h = options.h ?? TournamentTitle.TITLE_H;
    const requested_font_size = options.font_size ?? 85;
    const localized_text = LocalizationManager.t( localization_key );
    const text = options.wrap_to_two_lines
      ? TournamentTitle.wrapToTwoLines( ctx, localized_text, requested_font_size, box_w )
      : localized_text;
    const font_size = TournamentTitle.fitFontSize( ctx, text, requested_font_size, box_w, box_h );
    const outline_scale = font_size / 85;

    return TournamentTitle.LAYERS.map( layer => new UITextElement(
      ctx,
      x,
      y,
      LayoutScaler.widthFromDesign( box_w ),
      LayoutScaler.heightFromDesign( box_h ),
      {
        ...( options.wrap_to_two_lines ? { text } : { localization_key } ),
        font_size: LayoutScaler.textSizeFromDesign( font_size ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        line_height: 0.9,
        scale_to_width: true,
        text_color: "#F4E55A",
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFACE" },
            { offset: 1, color: "#F4E55A" },
          ],
        },
        outline_width: LayoutScaler.textBorderSizeFromDesign( layer.outline_width * outline_scale ),
        outline_color: layer.outline_color,
        outline_offset_y: LayoutScaler.yFromDesign( layer.outline_offset_y * outline_scale ),
        ...( layer.shadow ? TournamentTitle.shadow( outline_scale ) : {} ),
      }
    ) );
  }

  /** Design font size at which `text` plus the outermost outline fits `box_w`, never above `font_size`. */
  private static fitFontSize( ctx: CanvasRenderingContext2D, text: string, font_size: number, box_w: number, box_h: number ): number
  {
    ctx.save();
    ctx.font = `900 ${font_size}px ${TextStyleScheme.geologica}`;
    const lines = text.split( "\n" );
    const text_w = Math.max( ...lines.map( line => ctx.measureText( line ).width ) );
    ctx.restore();

    const outline_w = TournamentTitle.LAYERS[0].outline_width * font_size / 85;
    const needed_w = text_w + outline_w;
    const width_scale = Math.min( 1, box_w / needed_w );
    const height_scale = Math.min( 1, box_h / ( lines.length * font_size * 0.9 ) );
    return Math.floor( font_size * Math.min( width_scale, height_scale ) );
  }

  /** Breaks an oversized localized title at the word boundary with the most balanced line widths. */
  private static wrapToTwoLines( ctx: CanvasRenderingContext2D, text: string, font_size: number, box_w: number ): string
  {
    const words = text.trim().split( /\s+/ );
    if ( words.length < 2 ) return text;

    ctx.save();
    ctx.font = `900 ${font_size}px ${TextStyleScheme.geologica}`;
    const outline_w = TournamentTitle.LAYERS[0].outline_width * font_size / 85;
    if ( ctx.measureText( text ).width + outline_w <= box_w )
    {
      ctx.restore();
      return text;
    }

    let best = text;
    let best_width = Number.POSITIVE_INFINITY;
    for ( let i = 1; i < words.length; i++ )
    {
      const first = words.slice( 0, i ).join( " " );
      const second = words.slice( i ).join( " " );
      const max_width = Math.max( ctx.measureText( first ).width, ctx.measureText( second ).width );
      if ( max_width < best_width )
      {
        best = `${first}\n${second}`;
        best_width = max_width;
      }
    }
    ctx.restore();
    return best;
  }

  public static createLabel( ctx: CanvasRenderingContext2D, x: number, y: number ): UITextElement
  {
    return new UITextElement(
      ctx,
      x,
      y,
      LayoutScaler.widthFromDesign( TournamentTitle.LABEL_W ),
      LayoutScaler.heightFromDesign( TournamentTitle.LABEL_H ),
      {
        localization_key: "TOURNAMENT",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.tournament_label_outline,
        ...TournamentTitle.shadow(),
      }
    );
  }
}
